import { React, useState, useEffect, useMemo } from 'react';
import PhotoPlaceholder from '../PagesClaude/placeholder';



const Gallery = ({ pages, filters, openLightbox, images }) => {

	const [island, setIsland] = useState("All");
	const [year, setYear] = useState("All");
	const [country, setCountry] = useState("All");

	// eslint-disable-next-line
	const [view, setView] = useState("grid"); // mosaic | grid | list
	
	useEffect(() => {
		window.scrollTo(0,0);
	}, []);
	
	const items = useMemo(() => {
		return images.filter(
			(p) =>
				(island === "All" || p.title === island) &&
				(year === "All" || parseInt(p.date_content.split("-")[2]) === year) &&
				(country === "All" || p.country === country)
		);
	}, [images,island, year, country]);
	
	
	
	return (
		<main className="wi-gallery">

			<header className="wi-gallery-head">
				<div className="wi-mono wi-eyebrow">— The archive</div>
				<h1 className="wi-gallery-title" data-insert-html dangerouslySetInnerHTML={{__html: pages.find(p => p.slug === "gallery-top").subtitle}}></h1>
				<p className="wi-gallery-lede" data-insert-html dangerouslySetInnerHTML={{__html: pages.find(p => p.slug === "gallery-top").description}}></p>
			</header>
			
			<div className="wi-filter-bar">
				<Filter label="Island" value={island} options={filters.islands} onChange={setIsland} />
				<Filter label="Country" value={country} options={filters.countries} onChange={setCountry} />
				<Filter label="Year" value={year} options={filters.years} onChange={setYear} />
				
				<div className="wi-filter-spacer" />
					
				<div hidden className="wi-view-toggle" role="tablist" aria-label="View mode">
					{[
						{ id: "mosaic", label: "Mosaic" },
						{ id: "grid", label: "Grid" },
						{ id: "list", label: "List" },
					].map((v) => (
						<button
							key={v.id}
							role="tab"
							aria-selected={view === v.id}
							className={`wi-view-btn ${view === v.id ? "is-on" : ""}`}
							onClick={() => setView(v.id)}
						>
							{v.label}
						</button>
					))}
				</div>
		
				<div className="wi-mono wi-count">
					{String(items.length).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
				</div>

			</div>
			
			{view === "mosaic" && <MosaicGrid items={items} openLightbox={openLightbox} />}
			{view === "grid" && <UniformGrid items={items} openLightbox={openLightbox} />}
			{view === "list" && <ListView items={items} openLightbox={openLightbox} />}
			
			{items.length === 0 && (
				<div className="wi-empty">
					<p className="wi-italic">No plates match this selection.</p>
					<button
						className="wi-link"
						onClick={() => {
							setIsland("All");
							setYear("All");
							setCountry("All");
						}}
					>
						Reset filters →
					</button>
				</div>
			)}

		</main>
	);
};




const Filter = ({ label, value, options, onChange }) => (
	<label className="wi-filter">
		<span className="wi-mono wi-filter-k">{label}</span>
		<select
			className="wi-filter-select"
			value={value}
			onChange={(e) => {
				const v = e.target.value;
				onChange(isNaN(Number(v)) || v === "All" ? v : Number(v));
			}}
		>
			{options.map((o) => (
				<option key={o} value={o}>
					{o}
				</option>
			))}
		</select>
	</label>
);




// Editorial mosaic — varied row heights driven by photo ratios
const MosaicGrid = ({ items, openLightbox }) => {
	// Group into rows of 3 with intentional variety
	const rows = [];

	for (let i = 0; i < items.length; i += 3){
		rows.push(items.slice(i, i + 3));
	}
	

	return (
		<div className="wi-mosaic">
			{rows.map((row, ri) => (
				<div key={ri} className={`wi-mosaic-row pattern-${ri % 3}`}>
				{row.map((p, ci) => (
					<button
						key={p.id}
						className="wi-mosaic-cell"
						onClick={() => openLightbox(p.id, items)}
					>
						<div className="wi-mosaic-frame">
							<PhotoPlaceholder
								src={p.cdnUrl} 
								caption={p.title}
								ratio={p.ratio}
								index={ri * 3 + ci}
								showMeta={false}
								imgStyle={{objectFit: "contain", filter: 'none' }}
							/>
						</div>
						<div className="wi-mosaic-cap">
							<div className="wi-mosaic-cap-l">
								<span className="wi-mono wi-mosaic-no">
									{p.pricingsJson.sku}
								</span>
								<span className="wi-mosaic-title">{p.title}</span>
							</div>
							<div className="wi-mono wi-mosaic-place">
								{p.country} · {p.date_content.split('-')[2]}
							</div>
						</div>
					</button>
				))}
				</div>
			))}
		</div>
	);
};

const UniformGrid = ({ items, openLightbox }) => (
	
	<div className="wi-uniform">
		{items.map((p, i) => (
			<button
				key={p.id}
				className="wi-uniform-cell"
				onClick={() => openLightbox(p.id, items)}
			>
				<PhotoPlaceholder 
					src={p.cdnUrl} 
					caption={p.caption} 
					ratio="3/4" 
					index={i} 
					showMeta={false} 
					imgStyle={{objectFit: "contain", filter: 'none' }}
				/>
				<span className="wi-mono wi-mosaic-no">
						{p.pricingsJson.sku}
					</span>
				<div className="wi-uniform-cap">
					<span>{p.title}</span>
					<span className="wi-mono">
						{p.data.country} · {p.date_content.split('-')[2]}
					</span>
				</div>
			</button>
		))}
	</div>
);

const ListView = ({ items, openLightbox }) => (
	<div className="wi-list">
		<div className="wi-list-head wi-mono">
			<span>Plate</span>
			<span>Title</span>
			<span>Island</span>
			<span>Made</span>
			<span></span>
		</div>
		{items.map((p, i) => (
			<button
				key={p.id}
				className="wi-list-row"
				onClick={() => openLightbox(p.id, items)}
			>
				<span className="wi-mono">{p.id.toUpperCase()}</span>
				<span className="wi-list-title">{p.title}</span>
				<span>{p.island}</span>
				<span className="wi-mono">{p.season} {p.year}</span>
				<span className="wi-list-arrow" aria-hidden>↗</span>
			</button>
		))}
	</div>
);



export default Gallery;
